import { isValidSubjectName, type DiscoveryAnswers, type V2Response } from '../api/v2TakeApi';

/**
 * The simulator's progress, kept only in this browser (no account, no row).
 * The one module that touches `localStorage` for it.
 *
 * Not per user: the flow is anonymous even for a logged-in visitor. A draft
 * from another schema or content version, or older than seven days since its
 * last save, is discarded rather than migrated — and `loadDraft` says so, so
 * the landing can tell the visitor instead of silently starting over.
 *
 * The finished simulation stays (`step: 'report'`) so the report's same-tab
 * links can be followed and come back; only Start over, expiry or a version
 * mismatch clear it.
 */

export const DRAFT_KEY = 'bquest.longevity-simulator.draft';
const SCHEMA_VERSION = 1;
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

/** A pillar index while taking it; `'report'` once all five are answered. */
export type DraftStep = 'discovery' | number | 'report';

export type SimulatorDraft = {
  schemaVersion: typeof SCHEMA_VERSION;
  /** `content.version` from the public GET the answers were given against. */
  contentVersion: string;
  savedAt: number;
  step: DraftStep;
  /** How the email gate was resolved; null until it is. */
  gate: 'email' | 'skipped' | null;
  discovery: DiscoveryAnswers;
  answers: Record<string, V2Response>;
  /**
   * The supporter's typed first name for the person they answer about, as
   * typed. Optional so a schema-1 draft saved before it existed still loads.
   */
  subjectName?: string;
};

export type DraftFields = Omit<SimulatorDraft, 'schemaVersion' | 'savedAt'>;

export type LoadedDraft = { draft: SimulatorDraft | null; discarded: boolean };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStep = (value: unknown): value is DraftStep =>
  value === 'discovery' ||
  value === 'report' ||
  (typeof value === 'number' && Number.isInteger(value) && value >= 0);

const isDraft = (value: unknown): value is SimulatorDraft =>
  isRecord(value) &&
  value.schemaVersion === SCHEMA_VERSION &&
  typeof value.contentVersion === 'string' &&
  typeof value.savedAt === 'number' &&
  isStep(value.step) &&
  (value.gate === null || value.gate === 'email' || value.gate === 'skipped') &&
  isRecord(value.discovery) &&
  Object.values(value.discovery).every(
    (keys) => Array.isArray(keys) && keys.every((key) => typeof key === 'string')
  ) &&
  isRecord(value.answers) &&
  Object.values(value.answers).every((response) => typeof response === 'string') &&
  (value.subjectName === undefined || typeof value.subjectName === 'string');

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Storage blocked (private window): there is nothing to clear.
  }
}

/**
 * The saved draft if it still fits `contentVersion`. Unreadable storage or
 * corrupt JSON reads as no draft; anything stored that no longer fits is
 * removed and reported as discarded.
 */
export function loadDraft(contentVersion: string, now = Date.now()): LoadedDraft {
  let parsed: unknown;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw === null) return { draft: null, discarded: false };
    parsed = JSON.parse(raw);
  } catch {
    return { draft: null, discarded: false };
  }

  if (
    isDraft(parsed) &&
    parsed.contentVersion === contentVersion &&
    now - parsed.savedAt <= EXPIRY_MS
  ) {
    // Storage is untrusted, and a draft can reopen past Discovery, where nothing
    // else checks the name: one the server would refuse is dropped here, so the
    // report request never 400s on it and reads as an expired simulation.
    if (parsed.subjectName !== undefined && !isValidSubjectName(parsed.subjectName)) {
      const { subjectName: _refused, ...kept } = parsed;
      return { draft: kept, discarded: false };
    }
    return { draft: parsed, discarded: false };
  }
  clearDraft();
  return { draft: null, discarded: true };
}

/** Best effort: with storage blocked the flow keeps working in memory. */
export function saveDraft(fields: DraftFields, now = Date.now()): void {
  const draft: SimulatorDraft = { ...fields, schemaVersion: SCHEMA_VERSION, savedAt: now };
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Quota or private window: progress lives only in memory this time.
  }
}
